import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  TextField,
  Alert,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import ThemePreviewDialog from './ThemePreviewDialog';
import ThemePurchaseCTA from './ThemePurchaseCTA';
import axios from 'axios';

// Theme Style Options (1-10)
const THEME_STYLES = [
  { id: 1, name: 'Modern', description: 'Clean, contemporary design with gradient accents', icon: '🎨' },
  { id: 2, name: 'Classic', description: 'Timeless, professional layout', icon: '📰' },
  { id: 3, name: 'Bold', description: 'Eye-catching with strong visual hierarchy', icon: '⚡' },
  { id: 4, name: 'Minimal', description: 'Simple, focused, distraction-free', icon: '✨' },
  { id: 5, name: 'Creative', description: 'Unique, artistic, expressive', icon: '🎭' },
  { id: 6, name: 'Elegant', description: 'Sophisticated and refined', icon: '💎' },
  { id: 7, name: 'Landing Page', description: 'Conversion-focused single-page layout', icon: '🚀' },
  { id: 8, name: 'Startup', description: 'Modern, growth-oriented design for new ventures', icon: '💡' },
  { id: 9, name: 'Corporate', description: 'Authoritative, structured business presence', icon: '🏢' },
  { id: 10, name: 'Portfolio', description: 'Showcase-first layout for work and projects', icon: '🖼️' }
];

// Color Schemes (1-10)
const COLOR_SCHEMES = [
  { id: 1, name: 'Professional Blue', primary: '#2563eb', secondary: '#1e40af', preview: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' },
  { id: 2, name: 'Modern Purple', primary: '#7c3aed', secondary: '#5b21b6', preview: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
  { id: 3, name: 'Fresh Green', primary: '#10b981', secondary: '#059669', preview: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
  { id: 4, name: 'Energetic Orange', primary: '#f59e0b', secondary: '#d97706', preview: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  { id: 5, name: 'Bold Red', primary: '#ef4444', secondary: '#dc2626', preview: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
  { id: 6, name: 'Elegant Gray', primary: '#6b7280', secondary: '#374151', preview: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)' },
  { id: 7, name: 'Midnight', primary: '#0f172a', secondary: '#1e293b', preview: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' },
  { id: 8, name: 'Ocean Teal', primary: '#0d9488', secondary: '#0f766e', preview: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' },
  { id: 9, name: 'Vibrant Pink', primary: '#ec4899', secondary: '#db2777', preview: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
  { id: 10, name: 'Warm Sand', primary: '#d97706', secondary: '#92400e', preview: 'linear-gradient(135deg, #d97706 0%, #92400e 100%)' }
];

// Font Pairings (1-5)
const FONT_PAIRINGS = [
  { id: 1, name: 'Professional Sans', heading: 'Inter', body: 'Inter', description: 'Modern, clean, readable' },
  { id: 2, name: 'Elegant Serif', heading: 'Playfair Display', body: 'Source Sans Pro', description: 'Classic, sophisticated' },
  { id: 3, name: 'Modern Minimal', heading: 'Poppins', body: 'Open Sans', description: 'Contemporary, friendly' },
  { id: 4, name: 'Tech Mono', heading: 'JetBrains Mono', body: 'IBM Plex Sans', description: 'Technical, precise' },
  { id: 5, name: 'Creative Mix', heading: 'Montserrat', body: 'Lato', description: 'Dynamic, versatile' }
];

// Pages included in the Quick Theme preview. All available pages are shown so
// users see the full breadth of what will be generated.
const QUICK_THEME_PREVIEW_PAGES = {
  'front-page.php': true,
  'about.php': true,
  'services.php': true,
  'contact.php': true,
  'page.php': true,
  'archive.php': true,
  'index.php': true
};

// Main DatabaseMode component
const DatabaseMode = () => {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedColorScheme, setSelectedColorScheme] = useState(0);
  const [selectedFontPairing, setSelectedFontPairing] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [generationResult, setGenerationResult] = useState(null); // { generationId, paymentStatus }
  const [error, setError] = useState(null);

  const handleStyleSelect = (style) => {
    setSelectedStyle(style);
    setError(null);
  };

  const handlePreview = async () => {
    if (!selectedStyle) {
      setError('Please select a theme style first');
      return;
    }
    setError(null);
    setPreviewError(null);
    setPreviewData(null);
    setPreviewOpen(true);
    setPreviewLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPreviewError('Authentication required. Please log in.');
        setPreviewLoading(false);
        return;
      }

      const colors = {
        primary: COLOR_SCHEMES[selectedColorScheme].primary,
        secondary: COLOR_SCHEMES[selectedColorScheme].secondary,
        // Quick Theme color schemes don't define a separate accent, so primary is reused
        accent: COLOR_SCHEMES[selectedColorScheme].primary
      };

      const fonts = {
        heading: FONT_PAIRINGS[selectedFontPairing].heading,
        body: FONT_PAIRINGS[selectedFontPairing].body
      };

      const response = await fetch('/api/generate/quick-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pages: QUICK_THEME_PREVIEW_PAGES,
          colors,
          fonts,
          themeId: selectedStyle.id,
          colorSchemeId: COLOR_SCHEMES[selectedColorScheme].id,
          fontPairingId: FONT_PAIRINGS[selectedFontPairing].id
        })
      });

      if (!response.ok) {
        let backendMessage = `Preview request failed (HTTP ${response.status})`;
        try {
          const errBody = await response.json();
          backendMessage = errBody.message || errBody.error || backendMessage;
        } catch {
          // ignore JSON parse failure
        }
        throw new Error(backendMessage);
      }

      const data = await response.json();
      if (!data.pages || Object.keys(data.pages).length === 0) {
        throw new Error('No preview pages were generated. The theme template files may be missing or unavailable on the server. Please contact support or try again later.');
      }
      setPreviewData(data);
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewError(err.message || 'Failed to generate preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedStyle) {
      setError('Please select a theme style first');
      return;
    }

    if (!businessName.trim()) {
      setError('Please enter your business name');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGenerationResult(null);
    setGenerationProgress(null);
    setGenerationStatus('Starting Quick Theme generation…');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in.');
        setIsGenerating(false);
        return;
      }

      // Send semantic data - backend now handles both formats
      const payload = {
        themeId: selectedStyle.id,
        colorScheme: COLOR_SCHEMES[selectedColorScheme],
        fontPairing: FONT_PAIRINGS[selectedFontPairing],
        businessName: businessName.trim()
      };

      console.log('📤 Sending payload:', payload);

      const response = await fetch('/api/generate/database', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Theme generation started:', result);
        const generationId = result.generationId;
        setGenerationStatus('Quick Theme generation in progress… This may take a few minutes.');
        // Stay on page and poll for completion instead of redirecting
        pollGenerationStatus(generationId);
      } else {
        console.error('❌ Generation failed:', result);
        setError(`Generation failed: ${result.message || result.error || 'Unknown error'}`);
        setIsGenerating(false);
      }
    } catch (err) {
      console.error('💥 Generation error:', err);
      setError('Failed to start theme generation. Please try again.');
      setIsGenerating(false);
    }
  };

  // Poll /api/generate/status/:id until the generation reaches a terminal state
  const pollGenerationStatus = (generationId) => {
    const token = localStorage.getItem('token');
    let resolved = false; // local flag to avoid stale-closure issues in the timeout

    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await axios.get(`/api/generate/status/${generationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { status, paymentStatus, canDownload, retryCount, emoji, message, subtext, progress } =
          statusResponse.data;

        if (status === 'completed') {
          resolved = true;
          clearInterval(pollInterval);
          setIsGenerating(false);
          setGenerationProgress(100);
          setGenerationStatus('✅ Quick Theme generated successfully!');
          setGenerationResult({
            generationId,
            paymentStatus: paymentStatus || 'pending',
            canDownload: canDownload || false,
          });
        } else if (status === 'failed') {
          resolved = true;
          clearInterval(pollInterval);
          setIsGenerating(false);
          if (retryCount < 3) {
            setError(`Generation failed. You have ${3 - retryCount} free retries remaining.`);
            setGenerationResult({ generationId, canRetry: true, retryCount });
          } else {
            setError('Generation failed after 3 attempts. Please contact support.');
          }
        } else {
          // Update the in-progress status message and progress bar
          if (progress !== null && progress !== undefined) {
            setGenerationProgress(progress);
          }
          if (emoji && message) {
            setGenerationStatus(subtext ? `${emoji} ${message}\n${subtext}` : `${emoji} ${message}`);
          }
        }
      } catch (pollErr) {
        console.error('Status poll error:', pollErr);
      }
    }, 5000); // poll every 5 seconds

    // Stop polling after 20 minutes; only report timeout if not already resolved
    setTimeout(() => {
      clearInterval(pollInterval);
      if (!resolved) {
        setIsGenerating(false);
        setError('Generation timeout. Please check your generation history in Projects.');
      }
    }, 1200000);
  };

  const handlePreviewGenerate = async () => {
    setPreviewOpen(false);
    await handleGenerate();
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          Quick Themes
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '1rem' }}>
          Choose a style, pick your colors and fonts, and we'll apply your business name and branding. Popular combinations load in seconds after the first run.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Business Name Input */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Business Name"
          placeholder="Enter your business name"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          variant="outlined"
          required
        />
      </Box>

      {/* Theme Style Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          1. Select Theme Style
        </Typography>
        <Grid container spacing={2}>
          {THEME_STYLES.map((style) => (
            <Grid item xs={12} sm={6} md={4} key={style.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedStyle?.id === style.id ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  '&:hover': { borderColor: '#2563eb' }
                }}
                onClick={() => handleStyleSelect(style)}
              >
                <CardContent>
                  <Typography variant="h4" sx={{ mb: 1 }}>{style.icon}</Typography>
                  <Typography variant="h6" gutterBottom>{style.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {style.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Color Scheme Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          2. Choose Color Scheme
        </Typography>
        <Grid container spacing={2}>
          {COLOR_SCHEMES.map((scheme, index) => (
            <Grid item xs={6} sm={4} md={2} key={scheme.id}>
              <Box
                onClick={() => setSelectedColorScheme(index)}
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  p: 2,
                  border: selectedColorScheme === index ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  borderRadius: 1,
                  '&:hover': { borderColor: '#2563eb' }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 60,
                    background: scheme.preview,
                    borderRadius: 1,
                    mb: 1
                  }}
                />
                <Typography variant="caption">{scheme.name}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Font Pairing Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          3. Select Font Pairing
        </Typography>
        <Grid container spacing={2}>
          {FONT_PAIRINGS.map((pairing, index) => (
            <Grid item xs={12} sm={6} md={4} key={pairing.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedFontPairing === index ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  '&:hover': { borderColor: '#2563eb' }
                }}
                onClick={() => setSelectedFontPairing(index)}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontFamily: pairing.heading, mb: 1 }}>
                    {pairing.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: pairing.body }}>
                    {pairing.description}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Heading: {pairing.heading} / Body: {pairing.body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handlePreview}
          disabled={!selectedStyle || previewLoading || isGenerating}
          startIcon={previewLoading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
        >
          {previewLoading ? 'Previewing…' : 'Preview Theme'}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerate}
          disabled={!selectedStyle || !businessName.trim() || isGenerating}
        >
          {isGenerating ? 'Generating…' : 'Generate Theme'}
        </Button>
      </Box>

      {/* Post-generation result panel */}
      {generationResult && !generationResult.canRetry && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: '#f0f9ff',
            border: '2px solid #2563eb',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="primary" gutterBottom>
            🎉 Quick Theme Generated!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your theme is ready. Complete payment to download it, or find it any time in your Projects folder.
          </Typography>

          <ThemePurchaseCTA
            projectId={generationResult.generationId}
            projectMode="quick"
            paymentStatus={generationResult.paymentStatus}
            projectStatus="completed"
            context="generation"
          />

          <Button
            component={RouterLink}
            to="/projects"
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
          >
            View in Projects
          </Button>
        </Box>
      )}

      {/* Retry result panel */}
      {generationResult && generationResult.canRetry && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: '#fef2f2',
            border: '2px solid #dc2626',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            ⚠️ Generation Failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No charge applied. You have {3 - generationResult.retryCount} free retries remaining.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
          >
            Retry Generation (Free)
          </Button>
        </Box>
      )}

      {/* Inline Generation Status Panel (replaces blocking modal) */}
      {isGenerating && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            bgcolor: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: 2,
            color: '#0c4a6e',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={22} thickness={4} />
            <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
              {generationStatus || 'Generating your theme…'}
            </Typography>
          </Box>
          <LinearProgress
            variant={generationProgress !== null ? 'determinate' : 'indeterminate'}
            value={generationProgress !== null ? generationProgress : undefined}
            sx={{ borderRadius: 1, height: 6 }}
          />
          {generationProgress !== null && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
              {generationProgress}%
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            💡 Quick Themes are cached — later requests for the same combination will be much faster.
          </Typography>
        </Box>
      )}

      {/* Preview Dialog */}
      <ThemePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onGenerate={handlePreviewGenerate}
        previewData={previewData}
        previewLoading={previewLoading}
        previewError={previewError}
        title="Quick Theme Preview"
      />
    </div>
  );
};

export default DatabaseMode;
